import { apiGetEventCategory, apiGetEventDetail } from "@/api/api";
import { findTopicById, useTopicStore } from "@/store/topic";
import { useRouter } from "next/navigation";
import { useMemo } from "react";
import { get } from "./utils";

export const skipRoutes = [ "/guide", "/event-preview", "/invitee-sign-in" ];

/**
 * 按配置 URL 跳转：站内路径用 router.push，predik/yn- 同域用 path 做 push，其余用 location.href
 * 用于 Banner、金刚区活动等统一跳转逻辑
 */
export function navigateByUrl( router: { push: ( url: string ) => void | Promise<void> }, url: string ): void {
    if ( url.startsWith( "/" ) ) {
        void router.push( url );
        return;
    }
    try {
        const parsedUrl = new URL( url );
        const hostname = parsedUrl.hostname;
        if ( hostname.includes( "predik" ) || hostname.includes( "yn-" ) ) {
            void router.push( url.replace( parsedUrl.origin, "" ) );
            return;
        }
    } catch {
        // invalid URL, fallback to location.href
    }
    window.location.href = url;
}

interface NavigateToEventDetailOptions {
    eventNo: string;
    commentId?: string | number;
    guessNo?: string;
    guessType?: number;
    isMainPageEvent?: boolean;
    eventCategory?: number;
}

interface NavigateToActivityOptions {
    relationType: "event" | "guess" | "activity" | "category";
    eventNo?: string;
    guessNo?: string;
    jumpUrl?: string; // 活动类型仅用 url 跳转
    categoryId?: number; // 事件分类 id（一级或二级）
}

/**
 * 统一的详情跳转方法
 * @param router - Next.js router 实例
 * @param options - 跳转选项
 * @param options.eventNo - 事件ID（必填）
 * @param options.commentId - 评论ID（选填）
 * @param options.guessNo - 猜选项ID（选填）
 * @param options.guessType - 猜选项类型（选填，1: Yes, 2: No）
 * @param options.isMainPageEvent - 是否为主页内事件（选填，默认false）
 * @param options.eventCategory - 事件分类（选填，如果传入则跳过API调用）
 */
export async function navigateToEventDetail(
    router: { push: ( url: string ) => void | Promise<void> },
    options: NavigateToEventDetailOptions | string,
    commentId?: string | number,
    isMainPageEvent?: boolean,
) {
    // 兼容旧的调用方式：navigateToEventDetail(router, eventNo, commentId?, isMainPageEvent?)
    let eventNo: string;
    let finalCommentId: string | number | undefined;
    let finalGuessNo: string | undefined;
    let finalGuessType: number | undefined;
    let finalIsMainPageEvent: boolean;
    let finalEventCategory: number | undefined;

    if ( typeof options === "string" ) {
        // 旧式调用：第一个参数是 eventNo
        eventNo = options;
        finalCommentId = commentId;
        finalIsMainPageEvent = isMainPageEvent ?? false;
        finalEventCategory = undefined;
    } else {
        // 新式调用：第一个参数是选项对象
        eventNo = options.eventNo;
        finalCommentId = options.commentId;
        finalGuessNo = options.guessNo;
        finalGuessType = options.guessType;
        finalIsMainPageEvent = options.isMainPageEvent ?? false;
        finalEventCategory = options.eventCategory;
    }

    if ( !eventNo ) {
        console.warn( "navigateToEventDetail: eventNo is required" );
        return;
    }

    // 判断 eventCategory 是否已传入
    // 注意：传0也算传入了，传undefined或null都不算传入
    const hasEventCategory = finalEventCategory !== undefined && finalEventCategory !== null;

    let eventCategory = 0; // 默认普通事件
    if ( hasEventCategory ) {
        // 如果已传入（包括0），直接使用传入的值
        eventCategory = finalEventCategory as number;
    } else {
        // 如果未传入，调用API获取事件分类
        const [ isOk, res ] = await apiGetEventCategory( eventNo );
        if ( isOk && res?.data !== undefined ) {
            eventCategory = get( res, "data", 0 );
        }
    }
    console.log( eventCategory, "eventCategory" );

    // 根据 isMainPageEvent 和 eventCategory 决定跳转路径
    let basePath: string;
    if ( finalIsMainPageEvent ) {
        // 主页内事件：根据 eventCategory 跳转到 /main-event-detail 或 /sport-event-detail
        basePath = eventCategory === 0 ? "/main-event-detail" : "/sport-event-detail";
    } else {
        // 独立页面事件：根据 eventCategory 跳转到 /event-detail 或 /sport-event-detail
        basePath = eventCategory === 0 ? "/event-detail" : "/sport-event-detail";
    }

    // 构建查询参数
    const params = new URLSearchParams();
    params.set( "eventno", eventNo );
    params.set( "eventCategory", eventCategory.toString() );
    if ( finalCommentId ) {
        params.set( "comment", String( finalCommentId ) );
    }
    if ( finalGuessNo ) {
        params.set( "guessno", finalGuessNo );
    }
    if ( finalGuessType !== undefined ) {
        params.set( "guesstype", String( finalGuessType ) );
    }
    if ( basePath === "/main-event-detail" ) {
        const topicId = useTopicStore.getState().selectedNormalTopicId;
        if ( topicId !== null ) {
            params.set( "topicId", String( topicId ) );
        }
    }
    if ( eventCategory === 5 ) {
        basePath = "/event";
    }

    // 执行跳转
    const url = `${basePath}?${params.toString()}`;
    void router.push( url );
}

/**
 * 增强版的 Next.js router hook
 * 返回一个 router 对象，其中 back 方法包含智能回退逻辑
 * 如果浏览器历史记录中没有上一页（例如直接打开的 URL），则导航到首页
 */
export function useAppRouter() {
    const nextRouter = useRouter();

    const router = useMemo( () => {
        return {
            // 直接代理原生方法
            push: nextRouter.push.bind( nextRouter ),
            replace: nextRouter.replace.bind( nextRouter ),
            refresh: nextRouter.refresh.bind( nextRouter ),
            prefetch: nextRouter.prefetch.bind( nextRouter ),
            forward: nextRouter.forward.bind( nextRouter ),

            // 增强的 back 方法
            back: ( fallbackPath: string = "/" ) => {
                if ( typeof window === "undefined" ) return;

                // 组合判断：
                // 1. history.length === 1: 肯定是第一页
                // 2. history.length === 2 + 无同源referrer: 很可能是直接打开的第一页
                // 3. history.length > 2: 肯定有历史记录
                const historyLength = window.history.length;
                const referrer = document.referrer;
                const hasSameOriginReferrer = referrer && new URL( referrer ).origin === window.location.origin;

                if ( historyLength === 1 || ( historyLength === 2 && !hasSameOriginReferrer ) ) {
                    nextRouter.replace( fallbackPath );
                } else {
                    nextRouter.back();
                }
            },
        };
    }, [ nextRouter ] );

    return router;
}

/**
 * 跳转首页并选中指定分类（先拉取并缓存 normalTopics，再选中后跳转）
 */
export async function navigateToHomeCategory(
    router: { push: ( url: string ) => void | Promise<void> },
    categoryId: number,
) {
    const store = useTopicStore.getState();
    if ( store.normalTopics.length === 0 ) {
        await store.fetchNormalTopics();
    }
    const topics = useTopicStore.getState().normalTopics;
    const topic = findTopicById( topics, categoryId );
    if ( !topic ) {
        console.warn( `navigateToHomeCategory: categoryId ${categoryId} not found in topic tree` );
        return;
    }
    // 若为一级分类（有子分类），则选中其下第一个二级/叶子，否则选中当前节点
    let target = topic;
    while ( target.children?.length ) {
        target = target.children[0];
    }
    store.setSelectedNormalTopic( target );
    void router.push( "/" );
}

/**
 * 活动/金刚区跳转方法
 * @param options.relationType - 事件、竞猜、活动、事件分类
 * @param options.jumpUrl - 活动类型仅用 url 跳转
 * @param options.categoryId - 事件分类时为一二级分类 id
 */
export async function navigateToActivity(
    router: { push: ( url: string ) => void | Promise<void> },
    options: NavigateToActivityOptions,
) {
    const { relationType, eventNo, guessNo, jumpUrl, categoryId } = options;

    if ( relationType === "event" ) {
        if ( !eventNo ) {
            console.warn( "navigateToActivity: eventNo is required when relationType is 'event'" );
            return;
        }
        await navigateToEventDetail( router, { eventNo } );
        return;
    }

    if ( relationType === "guess" ) {
        if ( !eventNo ) {
            console.warn( "navigateToActivity: eventNo is required when relationType is 'guess'" );
            return;
        }
        const [ isOkCat, resCat ] = await apiGetEventCategory( eventNo );
        const eventCategory = isOkCat && resCat?.data !== undefined ? get( resCat, "data", 0 ) : 0;
        if ( eventCategory === 1 ) {
            await navigateToEventDetail( router, { eventNo, isMainPageEvent: true, eventCategory } );
            return;
        }
        const [ isOkDetail, resDetail ] = await apiGetEventDetail( eventNo );
        const list = isOkDetail ? get( resDetail, "data.guessList", [] ) : [];
        const guessCount = Array.isArray( list ) ? list.length : 0;
        if ( guessCount > 1 && guessNo ) {
            const params = new URLSearchParams();
            params.set( "guessno", guessNo );
            params.set( "eventno", eventNo );
            void router.push( `/single-guess-detail?${params.toString()}` );
            return;
        }
        await navigateToEventDetail( router, { eventNo, isMainPageEvent: true, eventCategory } );
        return;
    }

    if ( relationType === "activity" ) {
        if ( !jumpUrl ) {
            console.warn( "navigateToActivity: jumpUrl is required when relationType is 'activity'" );
            return;
        }
        navigateByUrl( router, jumpUrl );
        return;
    }

    if ( relationType === "category" ) {
        if ( categoryId === undefined || categoryId === null ) {
            console.warn( "navigateToActivity: categoryId is required when relationType is 'category'" );
            return;
        }
        await navigateToHomeCategory( router, Number( categoryId ) );
    }
}

/**
 * 跳转到单竞猜详情页（/single-guess-detail）
 * @param router - Next.js router 实例
 * @param options - 跳转选项
 * @param options.guessNo - 竞猜ID（必填）
 * @param options.eventNo - 事件ID（必填）
 */
export function navigateToSingleGuessDetail(
    router: { push: ( url: string ) => void | Promise<void> },
    options: { guessNo: string; eventNo: string },
) {
    const params = new URLSearchParams();
    params.set( "guessno", options.guessNo );
    params.set( "eventno", options.eventNo );
    const url = `/single-guess-detail?${params.toString()}`;
    void router.push( url );
}

/**
 * 跳转到订单竞猜详情页
 * @param router - Next.js router 实例
 * @param options - 跳转选项
 * @param options.guessNo - 竞猜ID（必填）
 * @param options.eventNo - 事件ID（必填）
 */
export function navigateToOrderGuessDetail(
    router: { push: ( url: string ) => void | Promise<void>; replace?: ( url: string ) => void | Promise<void> },
    options: { guessNo: string; eventNo: string; guessGroupNo?: string; guessType?: 1 | 2; mode?: "push" | "replace" },
) {
    const params = new URLSearchParams();
    params.set( "guessno", options.guessNo );
    params.set( "eventno", options.eventNo );
    if ( options.guessType !== undefined ) {
        params.set( "guesstype", String( options.guessType ) );
    }
    if ( options.guessGroupNo ) {
        params.set( "guessGroupNo", options.guessGroupNo );
    }
    const url = `/order-guess-detail?${params.toString()}`;
    if ( options.mode === "replace" && router.replace ) {
        void router.replace( url );
        return;
    }
    void router.push( url );
}
